import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useVirtualizer } from '@tanstack/react-virtual';
import { memo, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Column } from '../../types/SprintBoardtypes';
import TaskCard from './TaskCard';

import { PlusOutlined } from '@ant-design/icons';
import colorTokens from '../../constants/design_system/colors';
import { statusColorMap, statusDarkerColorMap, statusTextColorMap } from '../../constants/design_system/statusColors';
import { ModalContext, ModalType } from '../../context/ModalContext';
import { useViewportHeight } from '../../hooks/useIsMobile';
import { useViewContext } from '../../pages/Space/Context/ViewContext';
import { TaskStatus } from '../../types/TaskStatus';
import { WorkspaceViewGroup } from '../../types/WorkspaceView';
import { TaskTableDataType } from '../TaskTable/types/TaskTableTypes';
import CustomTooltip from '../UI/Tooltip/CustomTooltip';

interface Props {
  column: Column;
  tasks: TaskTableDataType[];
  taskStatus: TaskStatus;
  addTaskToColumn: (statusId: number, addToTop: boolean) => void;
  isSprint: boolean | undefined;
  filterScrollKey: string;
}

function ColumnContainer({ column, tasks, taskStatus, addTaskToColumn, isSprint, filterScrollKey }: Props) {
  const [mouseIsOver, setMouseIsOver] = useState(false);
  const [hasInitiallyScrolled, setHasInitiallyScrolled] = useState(false);

  const color = statusColorMap[taskStatus.color];
  const darkerColor = statusDarkerColorMap[taskStatus.color];
  const textColor = statusTextColorMap[taskStatus.color];

  const { openModal } = useContext(ModalContext);
  const viewContext = useViewContext();

  const tasksIds = useMemo(() => {
    return tasks.map((task) => task.id);
  }, [tasks]);

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: tasks.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      const task = tasks[index];
      if (!task) return 182;

      // Base height for task card
      let estimatedHeight = 150;

      // Add height for parent task banner
      if (task.parentTaskId) {
        estimatedHeight += 38;
      }

      // Estimate title height (rough approximation based on character count)
      if (task.title) {
        const titleLength = task.title.length;
        // Assume ~28 characters per line at current card width
        const estimatedLines = Math.ceil(titleLength / 28);
        if (estimatedLines > 1) {
          estimatedHeight += 22;
        }
      }

      // Add bottom margin between cards (10px from pb-2.5 class)
      estimatedHeight += 10;

      return estimatedHeight;
    },
    overscan: 5,
    measureElement: (element) => {
      // Ensure we're measuring after layout is complete
      const height = element.getBoundingClientRect().height;
      return height;
    },
    lanes: 1,
  });

  // Track task changes that affect height to trigger remeasurement
  const taskHeightKey = useMemo(
    () => tasks.map((t) => `${t.id}-${t.title?.length || 0}-${t.parentTaskId || 0}`).join(','),
    [tasks]
  );

  useLayoutEffect(() => {
    rowVirtualizer.measure();
    const timeoutId = setTimeout(() => {
      rowVirtualizer.measure();
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [taskHeightKey, rowVirtualizer]);

  const createTask = () => {
    openModal(ModalType.CreateItem, {
      status: taskStatus,
      sprintStatus: viewContext?.view?.group === WorkspaceViewGroup.sprint ? 'sprint' : undefined,
      insertBeforeTaskId: tasks[0]?.id,
    });
  };

  const { setNodeRef, attributes, listeners, transform, transition } = useSortable({
    id: column.status.id,
    data: {
      type: 'Column',
      column,
    },
    disabled: true,
  });

  const divRef = useRef<HTMLDivElement>(null);

  // Scroll to top only on initial load when tasks are first drawn
  useLayoutEffect(() => {
    if (divRef.current && tasks.length > 0 && !hasInitiallyScrolled) {
      divRef.current.scrollTo({ top: 0, behavior: 'auto' });
      setHasInitiallyScrolled(true);
    }
  }, [tasks.length, hasInitiallyScrolled]);

  // Scroll this column back to top whenever a filter (search or any other filter) is applied,
  // mirroring the outer board scroll reset in Board.tsx. #2524
  useEffect(() => {
    if (parentRef.current && hasInitiallyScrolled) {
      parentRef.current.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [filterScrollKey, hasInitiallyScrolled]);

  const viewportHeight = useViewportHeight();

  const style = {
    transition,
    transform: CSS.Translate.toString(transform),
    backgroundColor: color,
    maxHeight: `${viewportHeight - 155}px`,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="w-[318px] rounded-lg justify-between pb-3"
      onMouseEnter={() => {
        setMouseIsOver(true);
      }}
      onMouseLeave={() => {
        setMouseIsOver(false);
      }}
    >
      {/* Column title */}
      <div
        {...attributes}
        {...listeners}
        className="bg-mainBackgroundColor text-md h-[60px] rounded-md rounded-b-none p-3 border-columnBackgroundColor border-4 items-center text-brand-grey-dark text-sm font-semibold font-['Figtree'] select-none overflow-hidden"
      >
        <div className="flex justify-between overflow-hidden">
          <div className="justify-between items-center gap-2 inline-flex">
            <div className={`w-2 h-2 ${color} rounded-full`} />
            <div className="max-w-[215px] text-brand-grey-dark text-sm font-semibold whitespace-nowrap overflow-hidden text-ellipsis">
              {column.status.title}
            </div>
            <div
              style={{ backgroundColor: darkerColor }}
              // Grow with the count instead of a fixed w-6, which clipped 3–4 digit totals
              // outside the pill; min-w keeps single digits square. #2695
              className="min-w-[24px] h-6 px-1.5 bg-orange-200 rounded flex-col justify-center items-center gap-2.5 inline-flex"
            >
              <div className="text-brand-grey-dark text-sm font-medium leading-tight">{tasks.length}</div>
            </div>
          </div>

          {mouseIsOver && (
            <div className="w-6 h-6 p-1 bg-white rounded shadow border border-neutral-300 justify-start items-center gap-1 inline-flex mr-1">
              <CustomTooltip title="New Task">
                <PlusOutlined onClick={createTask} className={`${textColor}`} />
              </CustomTooltip>
            </div>
          )}
        </div>

        <div style={{ backgroundColor: textColor }} className="h-px mt-2 mx-2" />
      </div>

      {/* Column task container */}
      <div
        ref={parentRef}
        style={{ maxHeight: `${viewportHeight - (isSprint ? 273 : 225)}px`, scrollBehavior: 'auto' }}
        className="flex flex-grow flex-col px-4 overflow-x-hidden overflow-y-auto scroll-smooth"
      >
        <SortableContext items={tasksIds} strategy={verticalListSortingStrategy}>
          {tasks.length > 50 ? (
            // Virtualized rendering for large lists (50+ tasks)
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                const task = tasks[virtualItem.index];
                if (!task) return null;
                return (
                  <div
                    key={task.id}
                    data-index={virtualItem.index}
                    ref={rowVirtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <div className="pb-2.5">
                      <TaskCard task={task} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Normal rendering for small lists (< 50 tasks)
            <>
              {tasks.map((task) => (
                <div key={task.id} className="pb-2.5">
                  <TaskCard task={task} />
                </div>
              ))}
            </>
          )}
        </SortableContext>

        {tasks.length === 0 && (
          <div className="flex flex-col items-center justify-center pt-4 mb-3">
            <div className="text-center text-neutral-900 text-xs font-semibold font-['Figtree'] leading-none">
              No tasks with this status.
              <br />
              Drag tasks here or create new ones
            </div>
            <div
              className="w-[94px] h-6 px-2 py-2 bg-white rounded shadow border border-neutral-300 justify-start items-center gap-1 inline-flex mt-3 cursor-pointer"
              onClick={createTask}
            >
              <PlusOutlined className="w-4 h-4 relative" style={{ color: colorTokens.neutral[900] }} />
              <div className="text-neutral-900 text-sm font-medium font-['Figtree'] leading-none">Add Task</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Memoize ColumnContainer to prevent re-renders when other columns update
export default memo(ColumnContainer);
